import React, { useMemo, useState } from 'react';
import AgGrid from '../../components/ag-grid/ag-grid';
import { Title } from 'react-admin';
import { FilterListProvider } from '../../context/FilterListProvider';
import RenderControls from '../../components/ra-list/RenderControls';
import { dayjs, formatDateTime, formatLocalTime, toLocalTime } from '../../components/ex_dayjs';
import { IconButton, Link, Tooltip } from '@mui/material';
import './css/style.css';
import { AddLink, Check, HistoryOutlined } from '@mui/icons-material';
import MappingEvent from './components/map-event';
import History, { ListPriceHistory } from './components/history';

const ListingVisibility = () => {
    const refbtnFilter = React.useRef(null);
    const gridRef = React.useRef(null);
    const [defaultFilterValue] = useState({ sh_shown_eq: null, vs_shown_eq: null, event_date: [dayjs()], matched: -1, action: 1 });
    const [defaultValuesAG] = useState({
        sortField: { field: 'listed_date', order: 'DESC' },
    });
    const [mapEvent, setMapEvent] = useState({});
    const [historyPopup, setHistoryPopup] = useState({ open: false, data: null });
    const [listPriceHistoryPopup, setListPriceHistoryPopup] = useState({ open: false, data: null });
    const [selectedAction, setSelectedAction] = useState(1); // Track selected action
    const matchedList = [{ id: -1, name: 'ANY' }, { id: 0, name: 'Unmatched' }, { id: 1, name: 'Matched' }];
    const actionList = [{ id: -1, name: 'ANY' }, { id: 0, name: 'Sold' }, { id: 1, name: 'Active' }, { id: 2, name: 'Un-Sold' }];

    const controls = [
        { component: 'number', name: 'vs_event_id_eq', label: 'VS EventId', xs: 1 },
        { component: 'text', name: 'siteId', label: 'Site ID', xs: 2 },
        { component: 'text', name: 'event', label: 'Event', xs: 2 },
        { component: 'text', name: 'venue', label: 'Venue', xs: 2 },
        { component: 'daterange', name: 'event_date', label: 'Event Date' },
        { component: 'daterange', name: 'updated_on', label: 'Updated On' },
        { xs: 1 },
        //
        { component: 'number', name: 'id', label: 'InventoryId', xs: 1 },
        { component: 'text', name: 'section', label: 'Section', xs: 2 },
        { component: 'text', name: 'row', label: 'Row', xs: 2 },
        { component: 'select', name: 'matched', label: 'Matched', xs: 1, choices: matchedList, isEmptyOption: false },
        { component: 'select', name: 'action', label: 'Action', xs: 1, choices: actionList, isEmptyOption: false, onChange: (e) => setSelectedAction(e.value) },
        { component: 'checkbox', name: 'sh_shown_eq', label: 'SH-Shown', nullable: true },
        { component: 'checkbox', name: 'vs_shown_eq', label: 'VS-Shown', nullable: true }
    ];

    const defaultColDef = useMemo(() => {
        return {
            cellStyle: { wordBreak: 'normal', fontSize: '14px' },
            // singleClickEdit: true,
            sortable: true,
            wrapText: true,
            filter: false,
            autoHeight: true,
            flex: 1,
            suppressRowHoverHighlight: true,
            suppressHorizontalScroll: true,
            suppressMovable: true,//block move columns
            resizable: true,
            autoHeaderHeight: true,
        };
    }, []);

    const onCellClicked = (params) => {
        let priceColName = ['sh_price', 'vs_price'].find((col: any) => params.column.colId === col);
        if (priceColName) {
            let prefix = priceColName.split('_')[0];
            if (JSON.parse(params.data[prefix + '_competitor_json']).length > 0) {
                setHistoryPopup({ ...historyPopup, open: true, data: params.data[prefix + '_competitor_json'], btnId: `history-lv-${prefix}-${params.data.id}` });//history-lv-vs-736574473
            }
        }
        if (params.column.colId === 'list_price') {
            if (JSON.parse(params.data.list_price_history_json).length > 0) {
                setListPriceHistoryPopup({ open: true, data: params.data.list_price_history_json, btnId: `history-lv-lp-${params.data.id}` });
            }
        }
    };

    const colDefs = useMemo(() => {
        // Base columns
        const baseColumns = {
            headerName: '',
            children: [
                { field: 'id', headerName: 'Inventory ID', minWidth: 85, maxWidth: 95 },
                { field: 'event', headerName: 'Event', minWidth: selectedAction === 0 ? 220 : 260 },
                { field: 'venue', headerName: 'Venue', minWidth: selectedAction === 0 ? 220 : 260 },
                { field: 'event_date', headerName: 'EventDate', minWidth: 100, maxWidth: 100, valueFormatter: params => formatDateTime(params.value) },
                { field: 'section', headerName: 'Section', minWidth: 120, maxWidth: 130 },
                { field: 'row', headerName: 'Row', minWidth: 60, maxWidth: 70 },
                { field: 'seats', headerName: 'Seats', minWidth: 70, maxWidth: 80 },
                {
                    field: 'listed_date',
                    headerName: 'Listed Date',
                    minWidth: 85,
                    maxWidth: 95,
                    valueFormatter: params => formatDateTime(toLocalTime(params.value))
                },
                {
                    field: 'list_price',
                    headerName: 'ListPrice',
                    type: 'numericColumn',
                    minWidth: 90,
                    maxWidth: 110,
                    cellRenderer: (params) => {
                        const listPrice = params.data.list_price;
                        const originalPrice = params.data.original_price;
                        const hasHistory = JSON.parse(params.data.list_price_history_json ?? '[]').length > 0;

                        const formatPrice = (price) => !!price ? parseFloat(price).toFixed(2) : 'N/A';

                        return (
                            <span>
                                {hasHistory && <IconButton id={`history-lv-lp-${params.data.id}`} data-action='history-lv-lp' sx={{ padding: '2px' }}><HistoryOutlined sx={{ fontSize: '18px' }} /></IconButton>}
                                {`${formatPrice(listPrice)} (${formatPrice(originalPrice)})`}
                            </span>
                        );
                    }
                },
                { field: 'updated_on', headerName: 'UpdatedOn', minWidth: 85, maxWidth: 95, valueFormatter: params => formatDateTime(toLocalTime(params.value)) },
            ]
        };

        // Add sold-specific columns after updated_on when action is "Sold" (id: 0)
        if (selectedAction === 0) {
            baseColumns.children.push(
                {
                    field: 'order_date',
                    headerName: 'Sold Date',
                    minWidth: 85,
                    maxWidth: 95,
                    cellClass: 'highlight-column',
                    valueFormatter: params => formatDateTime(toLocalTime(params.value))
                },
                {
                    field: 'unitTicketSales',
                    headerName: 'Unit Ticket Sales',
                    headerClass: 'myag-header-cell-wrap-text',
                    minWidth: 100,
                    maxWidth: 110,
                    cellClass: ['highlight-column', 'myag-align-right'],
                    cellRenderer: params => params.value ? parseFloat(params.value).toFixed(2) : 'N/A'
                },
                {
                    field: 'profit',
                    headerName: 'Profit',
                    minWidth: 70,
                    maxWidth: 70,
                    cellClass: ['highlight-column', 'myag-align-right'],
                    // Profit = unitTicketSales - cost
                    cellRenderer: params => {
                        const unitTicketSales = params.data.unitTicketSales;
                        const cost = params.data.cost;
                        
                        const formatPrice = (price) => !!price ? parseFloat(price).toFixed(2) : 'N/A';

                        return `${formatPrice(unitTicketSales - cost)} (${formatPrice(cost)})`;
                    }
                }
            );
        }

        // StubHub columns
        const stubHubColumns = {
            headerName: 'StubHub',
            children: [
                {
                    field: 'sh_listing_id', headerName: 'ListingID', minWidth: 85, maxWidth: 95,
                    cellClass: 'myag-align-center',
                    cellRenderer: (params) => !params.value ?
                        <Tooltip title='Map event'><IconButton color="success" onClick={() => setMapEvent(prev => ({ ...prev, open: true, searchData: params.data, marketId: 3 }))}><AddLink /></IconButton></Tooltip>
                        : params.data.sh_url ? <Link href={params.data.sh_url} underline="hover">{params.value === 0 ? null : params.value}</Link> : params.value
                },
                {
                    field: 'sh_price', headerName: 'Price', type: 'numericColumn', minWidth: 105, maxWidth: 105,
                    cellRenderer: params => (
                        <span style={{ fontWeight: params.data.sh_competitor_json.length > 10 ? 'bold' : '' }}>
                            {JSON.parse(params.data.sh_competitor_json).length > 0
                            && <IconButton id={`history-lv-sh-${params.data.id}`} data-action='history-lv-sh' sx={{ padding: '2px' }}><HistoryOutlined sx={{ fontSize: '18px' }} /></IconButton>}
                            {params.value ? parseFloat(params.value).toFixed(2) : ''}
                            {/* Icon indicator for sh_shown status */}
                            {params.data.sh_shown === true && <Check sx={{ fontSize: '16px', marginLeft: '4px', verticalAlign: 'middle' }} />}
                        </span>
                    )
                },
            ]
        };

        // Add sh_shown and sh_map_price only when action is NOT "Sold"
        if (selectedAction !== 0) {
            stubHubColumns.children.push({
                field: 'sh_map_price', headerName: 'Map Price', type: 'numericColumn', minWidth: 85, maxWidth: 95
            });
        }

        // Vivid columns
        const vividColumns = {
            headerName: 'Vivid',
            children: [
                {
                    field: 'vs_listing_id', headerName: 'ListingID', minWidth: 100, maxWidth: 105,
                    cellClass: 'myag-align-center',
                    cellRenderer: (params) => !params.value ?
                        <Tooltip title='Map event'><IconButton color="success" onClick={() => setMapEvent(prev => ({ ...prev, open: true, searchData: params.data, marketId: 1 }))}><AddLink /></IconButton></Tooltip>
                        : params.data.vs_url ? <Link href={params.data.vs_url} underline="hover">{params.value === 0 ? null : params.value}</Link> : params.value
                },
                {
                    field: 'vs_price', headerName: 'Price', type: 'numericColumn', minWidth: 105, maxWidth: 105,
                    cellRenderer: params => (
                        <span style={{ fontWeight: params.data.vs_competitor_json.length > 10 ? 'bold' : '' }}>
                            {JSON.parse(params.data.vs_competitor_json).length > 0
                            && <IconButton id={`history-lv-vs-${params.data.id}`} data-action='history-lv-vs' sx={{ padding: '2px' }}><HistoryOutlined sx={{ fontSize: '18px' }} /></IconButton>}
                            {params.value ? parseFloat(params.value).toFixed(2) : ''}
                            {/* Icon indicator for vs_shown status */}
                            {params.data.vs_shown === true && <Check sx={{ fontSize: '16px', marginLeft: '4px', verticalAlign: 'middle' }} />}
                        </span>
                    )
                },
            ]
        };

        // Add vs_shown and vs_map_price only when action is NOT "Sold"
        if (selectedAction !== 0) {
            vividColumns.children.push({
                field: 'vs_map_price', headerName: 'Map Price', type: 'numericColumn', minWidth: 85, maxWidth: 95
            });
        }

        return [baseColumns, stubHubColumns, vividColumns];
    }, [selectedAction]);

    const handleSearch = (filterList) => {
        gridRef.current.customFilterSearch(filterList);
    };
    return (
        <>
            <Title title={'Listing Visibility'}></Title>
            <FilterListProvider>
                <RenderControls
                    // actions={}
                    controls={controls}
                    defaultValues={defaultFilterValue}
                    onSearch={handleSearch}
                    refFilter={refbtnFilter}
                    agSource={'ListingVisibility'}
                    agSort={defaultValuesAG.sortField}
                    exportFile={{
                        filename: `ListingVisibility-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                        columns: ['id', 'siteId', 'event', 'venue', 'event_date', 'section', 'list_price', 'original_price', 'updated_on', 'action', 'sh_listing_id', 'sh_url', 'sh_shown',
                            'sh_price', 'sh_map_price', 'vs_listing_id', 'vs_url', 'vs_shown', 'vs_price', 'vs_map_price'],
                        limit: 1000000
                    }}
                />
            </FilterListProvider>
            <AgGrid
                className='listing-visibility-grid'
                rowSelection={'multiple'}
                ref={gridRef}
                list={'ListingVisibility'}
                columnDefs={colDefs}
                defaultValues={defaultValuesAG}
                defaultColDef={defaultColDef}
                onCellClicked={onCellClicked}
            ></AgGrid>
            {!!mapEvent.searchData && <MappingEvent mapEvent={mapEvent} setMapEvent={setMapEvent} api={gridRef.current.getAgGridInstance().api} />}
            {historyPopup.open && <History info={historyPopup} setHistoryPopup={setHistoryPopup} />}
            {listPriceHistoryPopup.open && <ListPriceHistory info={listPriceHistoryPopup} setListPriceHistoryPopup={setListPriceHistoryPopup} />}
        </>
    );
};

export default ListingVisibility;